import type { ReactNode } from 'react';
import { teamOfficeIds } from '@realsite/site-config';
import type { Agent, AutomaticContentBlockProps, Office } from '../types';
import { formatPhone } from '../phone';
import { TeamsSection, type RosterTeam } from './TeamRoster';

/** The owner's own words on the roster's cards: the Automatic list section's settings (Dawn,
 *  2026-10-23: "Call/Text" above the phone numbers). Blank = the card's usual words. Also carries
 *  the section's one roster choice, `agentGrouping` (blank = one list). */
export type RosterWords = Pick<AutomaticContentBlockProps, 'phoneLabel' | 'emailLinkText' | 'profileLinkText' | 'officeLinkText' | 'agentGrouping'>;

const wordOr = (value: string | undefined, fallback: string): string => (value ?? '').trim() || fallback;

/** A phone link with the owner's words above it, when they set some. */
export function PhoneWithLabel({ label, children }: { label?: string; children: ReactNode }) {
  const words = (label ?? '').trim();
  if (!words) return <>{children}</>;
  return (
    <span className="tmpl-agent-phone inline-flex flex-col">
      <span className="tmpl-agent-phone-label text-xs font-semibold opacity-70">{words}</span>
      {children}
    </span>
  );
}

/**
 * The /agents page's live list: the agent roster (grouped by office) and the offices.
 *
 * One component for BOTH hosts. The tenant route renders it at the page's `automatic_content`
 * block; the Studio renders the same component in the same spot, with the same roster, so the
 * editor shows what visitors see. Before this the markup lived only in the tenant route and the
 * Studio drew a placeholder, while sections elsewhere drew the `agents` block's different card —
 * "the live page shows a different card than the preview" (Five Star Realty, 2026-10-15).
 *
 * Framework-agnostic (plain <a>) so it transpiles in both Next.js and Vite. The `tmpl-agent-*`
 * hooks take the site's Agent Card style (section_looks.agentCard → a `data-tmpl-agent-card`
 * ancestor + cardStyles.css), exactly as the home roster does.
 */

/** A roster row as GET /mls/agents returns it: the block `Agent` plus its page slug and offices. */
export interface RosterAgent extends Agent {
  /** URL slug for /agents/{slug} (stable after the roster's same-name dedup). */
  slug?: string | null;
  /** All offices this (merged) agent belongs to. */
  offices?: string[] | null;
}

/** The site profile, for the fallback card and office panel when the MLS gives no roster. */
export interface RosterProfile {
  full_name?: string | null;
  email?: string | null;
  phone?: string | null;
  headshot_url?: string | null;
  brokerage_name?: string | null;
}

/** Two-letter initials for the no-photo avatar. Tolerates a null/blank name (a roster row or a
 *  site profile with no name yet) — `name.split` on null threw and took the whole page to a 500. */
export function agentInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Uses the same `tmpl-agent-*` hooks as the home AgentsBlock so the site's chosen Agent Card style
// renders IDENTICALLY here and on the home roster. With no style selected the hooks are inert and
// this shows the default (photo-on-top) card.
export function RosterAgentCard({
  agent,
  primaryColor,
  officeNames,
  words,
}: {
  agent: RosterAgent;
  primaryColor: string;
  words?: RosterWords;
  /** Every office this (merged) agent belongs to. They are listed once, so the card carries it. */
  officeNames?: string[];
}) {
  const officeLine = officeNames && officeNames.length ? officeNames.join(' · ') : agent.office;
  return (
    <div className="tmpl-agent-card auto-list-card blk-card bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden flex flex-col">
      <div className="tmpl-agent-media relative overflow-hidden" style={{ backgroundColor: primaryColor + '10' }}>
        {agent.photo ? (
          <img
            src={agent.photo}
            alt={agent.name}
            className="tmpl-agent-img w-full h-56 object-cover"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div
            className="tmpl-agent-avatar w-full h-56 flex items-center justify-center text-white text-3xl font-bold"
            style={{ backgroundColor: primaryColor }}
          >
            {agentInitials(agent.name)}
          </div>
        )}
        {/* Hover reveal for the Overlay style — hidden unless a data-tmpl-agent-card ancestor turns it on */}
        <div className="tmpl-agent-reveal hidden" aria-hidden="true">
          <span className="tmpl-agent-reveal-name">{agent.name}</span>
          {agent.title ? <span className="tmpl-agent-reveal-title">{agent.title}</span> : null}
          {(agent.phone || agent.email) ? (
            <ul className="tmpl-agent-reveal-contact">
              {agent.phone ? <li>{formatPhone(agent.phone)}</li> : null}
              {agent.email ? <li>{wordOr(words?.emailLinkText, 'Email')}</li> : null}
            </ul>
          ) : null}
          <span className="tmpl-agent-reveal-cta">Contact</span>
        </div>
      </div>

      <div className="tmpl-agent-body p-4 flex-1 flex flex-col">
        <h3 className="tmpl-agent-name text-base font-heading font-bold text-gray-900 mb-0.5">
          {agent.slug ? (
            <a href={`/agents/${agent.slug}`} className="hover:underline">{agent.name}</a>
          ) : (
            agent.name
          )}
        </h3>
        {agent.title && (
          <p className="tmpl-agent-title text-xs font-semibold mb-1" style={{ color: primaryColor }}>
            {agent.title}
          </p>
        )}
        {officeLine && <p className="tmpl-agent-office text-xs text-gray-400 mb-2">{officeLine}</p>}

        <div className="tmpl-agent-contact mt-auto flex flex-wrap items-end gap-x-3 gap-y-1 pt-2 text-sm">
          {agent.phone && (
            <PhoneWithLabel label={words?.phoneLabel}>
              <a href={`tel:${agent.phone}`} className="tmpl-agent-link font-medium hover:underline" style={{ color: primaryColor }}>
                {formatPhone(agent.phone)}
              </a>
            </PhoneWithLabel>
          )}
          {agent.email && (
            <a href={`mailto:${agent.email}`} className="tmpl-agent-link font-medium hover:underline" style={{ color: primaryColor }}>
              {wordOr(words?.emailLinkText, 'Email')}
            </a>
          )}
          {agent.slug && (
            <a href={`/agents/${agent.slug}`} className="tmpl-agent-link inline-flex items-center gap-1 font-semibold hover:underline" style={{ color: primaryColor }}>
              {wordOr(words?.profileLinkText, 'View listings')}
              <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

const OfficeIcon = ({ color }: { color: string }) => (
  <svg className="w-5 h-5" style={{ color }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
  </svg>
);

const PhoneIcon = ({ className, style }: { className: string; style?: { color: string } }) => (
  <svg className={className} style={style} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
  </svg>
);

function OfficeCard({ office, primaryColor, index, agentCount, words }: { office: Office; index: number; primaryColor: string; agentCount: number; words?: RosterWords }) {
  const officeLabel = office.name || `Office ${index + 1}`;

  return (
    <div className="auto-list-card blk-card bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
      <div className="h-1.5" style={{ backgroundColor: primaryColor }} />
      <div className="p-6">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center mb-4"
          style={{ backgroundColor: primaryColor + '15' }}
        >
          <OfficeIcon color={primaryColor} />
        </div>
        <h3 className="text-base font-heading font-bold text-gray-900 mb-1">{officeLabel}</h3>
        {office.mlsId && (
          <p className="text-xs text-gray-400 mb-4">MLS ID: {office.mlsId}</p>
        )}
        {/* The office's head count. Its page lists the agents themselves, then its teams and listings. */}
        {agentCount > 0 && (
          <p className="text-sm font-semibold text-gray-700 mb-3" data-testid="office-agent-count">
            {agentCount} agent{agentCount !== 1 ? 's' : ''}
          </p>
        )}

        <div className="flex flex-col gap-2 text-sm mb-5">
          {office.phone && (
            <PhoneWithLabel label={words?.phoneLabel}>
              <a href={`tel:${office.phone}`} className="blk-link inline-flex items-center gap-2 text-gray-600 hover:underline">
                <PhoneIcon className="w-4 h-4 text-gray-400 shrink-0" />
                {formatPhone(office.phone)}
              </a>
            </PhoneWithLabel>
          )}
          {(office.address || office.city) && (
            <span className="inline-flex items-start gap-2 text-gray-600">
              <svg className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>
                {office.address && <span className="block">{office.address}</span>}
                {(office.city || office.state || office.zip) && (
                  <span className="block">
                    {[office.city, office.state].filter(Boolean).join(', ')}
                    {office.zip && ` ${office.zip}`}
                  </span>
                )}
              </span>
            </span>
          )}
        </div>

        {office.mlsId && (
          <a
            href={`/offices/${encodeURIComponent(office.mlsId)}`}
            className="blk-link inline-flex items-center gap-1.5 text-xs font-semibold transition-colors"
            style={{ color: primaryColor }}
          >
            {wordOr(words?.officeLinkText, "View this office's listings")}
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </a>
        )}
      </div>
    </div>
  );
}

const UNKNOWN_OFFICE = '__unknown__';

/** Every office id an agent belongs to (a merged agent can have several); never empty. */
export function agentOfficeIds(a: RosterAgent): string[] {
  const ids = a.offices && a.offices.length ? a.offices : (a.officeMlsId ? [a.officeMlsId] : []);
  return ids.length ? ids : [UNKNOWN_OFFICE];
}

export function AgentRoster({
  agents,
  offices,
  primaryColor,
  profile,
  companyName,
  words,
  teams = [],
}: {
  agents: RosterAgent[];
  offices: Office[];
  /** The site's teams, resolved against this roster (packages/site-config resolveTeams). */
  teams?: RosterTeam[];
  primaryColor: string;
  /** The site profile: the fallback card (when it has a name) and the fallback office panel. */
  profile?: RosterProfile | null;
  /** The office panel's name when the profile has no brokerage. */
  companyName: string;
  /** The owner's words on the cards (the Automatic list section's settings). */
  words?: RosterWords;
}) {
  // Build a lookup map from officeMlsId → office name. Agents carry their own office name, so
  // seed from them FIRST and let the offices endpoint overwrite: when that endpoint comes back
  // empty the heading fell through to a raw "Office 276650414", which is an internal id shown to
  // a visitor. The agent already told us the office is "Simon Gray Realty LLC".
  const officeNameByMlsId = new Map<string, string>();
  for (const agent of agents) {
    if (agent.officeMlsId && agent.office) officeNameByMlsId.set(agent.officeMlsId, agent.office);
  }
  for (const office of offices) {
    if (office.mlsId && office.name) officeNameByMlsId.set(office.mlsId, office.name);
  }

  // Named offices for a merged agent's card. Ids we have no name for are dropped rather than
  // shown as "Office 276650414" — an internal id is not information to a visitor.
  const officeNamesFor = (a: RosterAgent): string[] => {
    const names = agentOfficeIds(a)
      .filter((id) => id !== UNKNOWN_OFFICE)
      .map((id) => officeNameByMlsId.get(id))
      .filter((n): n is string => Boolean(n));
    if (names.length === 0 && a.office) names.push(a.office);
    return Array.from(new Set(names));
  };

  // ONE list by default: every agent from every feed and office, each person once (the gateway
  // merges the same person across feeds before this). Headings per office are the section's
  // "Group by office" choice, for a brokerage whose visitors look for one branch.
  //
  // The page used to always group, and an agent with no office id (a hand-added agent, or a feed
  // that names the office but not its id) got its own "Agents" heading under the office they
  // actually work at — Five Star Realty showed "Five Star Realty (5)" and "Agents (3)" for one
  // office (Dawn, 2026-10-29). Grouped, such an agent now joins the office whose NAME they carry.
  //
  // A merged agent can belong to several offices, and is listed once — under the first of them.
  // The card names every office they belong to instead, which keeps the information without the
  // double take. One person, one card.
  const officeIdByName = new Map<string, string>();
  for (const [id, name] of officeNameByMlsId) {
    const n = name.trim().toLowerCase();
    if (n && !officeIdByName.has(n)) officeIdByName.set(n, id);
  }
  const groupKeyFor = (agent: RosterAgent): string => {
    const first = agentOfficeIds(agent)[0];
    if (first !== UNKNOWN_OFFICE) return first;
    return officeIdByName.get((agent.office ?? '').trim().toLowerCase()) ?? UNKNOWN_OFFICE;
  };
  const agentsByOffice = new Map<string, RosterAgent[]>();
  for (const agent of agents) {
    const key = groupKeyFor(agent);
    const existing = agentsByOffice.get(key);
    if (existing) existing.push(agent);
    else agentsByOffice.set(key, [agent]);
  }
  const groupByOffice = words?.agentGrouping === 'office' && agentsByOffice.size > 1;
  // Office names on the cards only when there is more than one office to tell apart.
  const manyOffices = new Set(agents.map((a) => officeNamesFor(a).join('|'))).size > 1;

  const hasAgents = agents.length > 0;
  const fallbackName = (profile?.full_name ?? '').trim();
  const hasOffices = offices.length > 0;

  return (
    <>
      {/* Agents grid — one list, or grouped by office when the section asks */}
      {hasAgents ? (
        <section className="auto-list-band py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {groupByOffice ? (
              // Grouped by office — each group with a heading
              Array.from(agentsByOffice.entries()).map(([officeKey, officeAgents]) => {
                const officeName = officeKey !== UNKNOWN_OFFICE
                  ? (officeNameByMlsId.get(officeKey) ?? `Office ${officeKey}`)
                  : 'Other agents';
                return (
                  <div key={officeKey} className="mb-14 last:mb-0">
                    <h2 className="text-xl font-heading font-bold text-gray-900 mb-6 pb-3 border-b border-gray-100">
                      {officeName}
                      <span className="ml-2 text-sm font-normal text-gray-400">
                        ({officeAgents.length} agent{officeAgents.length !== 1 ? 's' : ''})
                      </span>
                    </h2>
                    <div className="auto-list-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                      {officeAgents.map((agent) => (
                        <RosterAgentCard
                          key={agent.key}
                          agent={agent}
                          primaryColor={primaryColor}
                          officeNames={officeNamesFor(agent)}
                          words={words}
                        />
                      ))}
                    </div>
                  </div>
                );
              })
            ) : (
              // One list — every office's agents together
              <div className="auto-list-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5">
                {agents.map((agent) => (
                  <RosterAgentCard
                    key={agent.key}
                    agent={agent}
                    primaryColor={primaryColor}
                    officeNames={manyOffices ? officeNamesFor(agent) : undefined}
                    words={words}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      ) : (
        /* Fallback: the site profile's agent card when no MLS agent data. Only when the profile
           has a name — an empty live roster is normal right after agents are added (they sit in the
           DRAFT until Publish), and a nameless profile used to 500 the page here. */
        fallbackName && profile && (
          <section className="auto-list-band py-16 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="auto-list-grid grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-5 max-w-4xl">
                <RosterAgentCard
                  primaryColor={primaryColor}
                  words={words}
                  agent={{
                    key: 'profile',
                    name: fallbackName,
                    email: profile.email ?? null,
                    phone: profile.phone ?? null,
                    photo: profile.headshot_url ?? null,
                    title: null,
                    office: profile.brokerage_name ?? null,
                    officeMlsId: null,
                  }}
                />
              </div>
            </div>
          </section>
        )
      )}

      {/* Teams — their own section, between the agents and the offices (Dawn, 2026-10-28) */}
      <TeamsSection
        teams={teams}
        primaryColor={primaryColor}
        className="auto-list-band py-16 bg-white border-t border-gray-100"
        officeNameFor={(t) => {
          const ids = teamOfficeIds(t);
          return ids.length === 1 ? officeNameByMlsId.get(ids[0]) : null;
        }}
      />

      {/* Offices */}
      {hasOffices && (
        <section className="auto-list-band py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-heading font-bold text-gray-900 mb-10 text-center">
              Our Offices
            </h2>
            <div className="auto-list-grid grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {offices.map((office, i) => (
                <OfficeCard key={office.key || office.mlsId || i} office={office} index={i} primaryColor={primaryColor} words={words} agentCount={office.mlsId ? agents.filter((a) => agentOfficeIds(a).includes(office.mlsId as string) || groupKeyFor(a) === office.mlsId).length : 0} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Fallback office section from the site profile when no MLS office data */}
      {!hasOffices && (
        <section className="auto-list-band py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-heading font-bold text-gray-900 mb-10 text-center">Our Office</h2>
            <div className="auto-list-card blk-card max-w-2xl mx-auto bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
              <div className="flex flex-col gap-5">
                <div className="flex items-start gap-4">
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                    style={{ backgroundColor: primaryColor + '18' }}
                  >
                    <OfficeIcon color={primaryColor} />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Office</p>
                    <p className="text-gray-800 font-medium">{profile?.brokerage_name || companyName}</p>
                  </div>
                </div>
                {profile?.phone && (
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: primaryColor + '18' }}>
                      <PhoneIcon className="w-5 h-5" style={{ color: primaryColor }} />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-0.5">Phone</p>
                      <a href={`tel:${profile.phone}`} className="blk-link text-gray-800 font-medium hover:underline">{profile.phone}</a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
